// Single source of truth for sensor-status presentation in the accounts feature.
//
// Sensor status from MongoDB V3 is a NUMBER (0..9). The UI also uses STRING
// keys (`'active'`, `'inactive'`, …) for filter dropdowns and URL params.
// This module owns the bidirectional mapping between code, key, label, and
// badge variant — replacing the duplicate maps that previously lived in
// `SensorsTab.tsx`, `ImportSensorsModal.tsx`, etc.

export type SensorStatusKey =
  | 'unmanaged'
  | 'active'
  | 'in_alert'
  | 'to_acknowledge'
  | 'inactive'
  | 'maintenance'
  | 'inhibited'
  | 'archived'
  | 'unknown';

export type SensorStatusVariant =
  | 'success'
  | 'warning'
  | 'secondary'
  | 'destructive'
  | 'danger'
  | 'purple'
  | 'orange'
  | 'gray'
  | 'darkgray'
  | 'blue'
  | 'yellow'
  | 'red';

export interface SensorStatusDescriptor {
  /** Canonical key used in filter values + URL params. */
  key: SensorStatusKey;
  /** Display label rendered in badges and dropdowns. */
  label: string;
  /** Badge color variant. */
  variant: SensorStatusVariant;
}

// Numeric → descriptor. Keys + colors match the V2 customer-dashboard exactly,
// so the table badge reads the same as the colored breakdown in the Sensors
// tab header. "Warning" no longer exists as a user-facing concept — status
// 2/3 split as "In Alert" (red), status 4 stands alone as "To Acknowledge"
// (yellow).
const BY_CODE: Record<number, SensorStatusDescriptor> = {
  0: { key: 'unmanaged', label: 'Unmanaged', variant: 'purple' },
  1: { key: 'active', label: 'Active', variant: 'success' },
  2: { key: 'in_alert', label: 'In Alert', variant: 'red' },
  3: { key: 'in_alert', label: 'In Alert', variant: 'red' },
  4: { key: 'to_acknowledge', label: 'To Acknowledge', variant: 'yellow' },
  5: { key: 'inactive', label: 'Inactive', variant: 'darkgray' },
  6: { key: 'maintenance', label: 'In Maintenance', variant: 'gray' },
  8: { key: 'inhibited', label: 'Alerts Inhibited', variant: 'orange' },
  9: { key: 'archived', label: 'Archived', variant: 'blue' },
};

// String key → descriptor (used when callers pass already-normalized keys).
const BY_KEY: Record<string, SensorStatusDescriptor> = {
  unmanaged: { key: 'unmanaged', label: 'Unmanaged', variant: 'purple' },
  active: { key: 'active', label: 'Active', variant: 'success' },
  in_alert: { key: 'in_alert', label: 'In Alert', variant: 'red' },
  to_acknowledge: { key: 'to_acknowledge', label: 'To Acknowledge', variant: 'yellow' },
  // Legacy alias — older saved filter sets may still emit 'warning'.
  warning: { key: 'to_acknowledge', label: 'To Acknowledge', variant: 'yellow' },
  inactive: { key: 'inactive', label: 'Inactive', variant: 'darkgray' },
  maintenance: { key: 'maintenance', label: 'In Maintenance', variant: 'gray' },
  inhibited: { key: 'inhibited', label: 'Alerts Inhibited', variant: 'orange' },
  archived: { key: 'archived', label: 'Archived', variant: 'blue' },
};

const UNKNOWN: SensorStatusDescriptor = { key: 'unknown', label: 'Unknown', variant: 'secondary' };

/**
 * Normalize any incoming sensor-status representation (raw number, numeric
 * string, or key string) to a `{ key, label, variant }` descriptor.
 *
 * Falls back to `{ key: 'unknown', label: 'Unknown' }` for unrecognized
 * values so callers never have to null-check the result.
 */
export function normalizeSensorStatus(raw: number | string | null | undefined): SensorStatusDescriptor {
  if (raw === null || raw === undefined || raw === '') return UNKNOWN;

  if (typeof raw === 'number') {
    return BY_CODE[raw] || UNKNOWN;
  }

  const trimmed = String(raw).trim().toLowerCase();
  if (trimmed === '') return UNKNOWN;

  // Numeric strings ('0', '1', ...) flow through the code map.
  if (/^\d+$/.test(trimmed)) {
    return BY_CODE[Number(trimmed)] || UNKNOWN;
  }

  return BY_KEY[trimmed] || { ...UNKNOWN, label: String(raw) };
}

/**
 * Filter dropdown options for the accounts Sensors tab (matches the keys the
 * backend's `statusKeys` query param accepts, including the synthetic
 * `pending_setup` value that the UI splits out from status=1 + empty stream).
 */
export const SENSOR_STATUS_FILTER_OPTIONS: ReadonlyArray<{ label: string; value: string }> = [
  { label: 'Unmanaged', value: 'unmanaged' },
  { label: 'Active', value: 'active' },
  { label: 'Pending Setup', value: 'pending_setup' },
  { label: 'Inactive', value: 'inactive' },
  // Split out from the legacy 'warning' bucket — server now accepts the
  // real statuses individually (see related.js statusKeys handling).
  { label: 'In Alert', value: 'in_alert' },
  { label: 'To Acknowledge', value: 'to_acknowledge' },
  { label: 'In Maintenance', value: 'maintenance' },
  { label: 'Alerts Inhibited', value: 'inhibited' },
  { label: 'Archived', value: 'archived' },
];

/**
 * Numeric-keyed entries for legacy modals that filter by raw status code
 * (e.g. ImportSensorsModal's status dropdown).
 */
export const SENSOR_STATUS_CODE_ENTRIES: ReadonlyArray<[number, SensorStatusDescriptor]> =
  Object.entries(BY_CODE).map(([code, descriptor]) => [Number(code), descriptor]);
